import { useToastContext } from "@ttc3k/trekker";
import { useOrgHome } from "apps/home/hooks";
import {
  EnumOrganizationEmailDomainStatus,
  OrganizationEmailDomainByOrganizationDocument,
  useOrganizationEmailDomainByOrganizationQuery,
  useOrganizationEmailDomainRequestMutation,
} from "gql/generated";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureFlag } from "shared/contextProviders/featureFlags";
import { FeatureFlags } from "shared/featureFlags";
import { usePostHog } from "shared/hooks";

const REQUESTABLE_STATUSES = new Set<EnumOrganizationEmailDomainStatus>([
  EnumOrganizationEmailDomainStatus.Requested,
  EnumOrganizationEmailDomainStatus.Disabled,
]);

export const useCustomEmailDomainPanel = () => {
  const { t } = useTranslation("home", {
    keyPrefix: "VIEWS.SETTINGS.VIEWS.LOGO.CUSTOM_EMAIL_DOMAIN",
  });
  const { t: tCore } = useTranslation();
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();
  const { organization } = useOrgHome();

  const flagEnabled = useFeatureFlag(FeatureFlags.CustomEmailDomain);
  const organizationId = organization?._id;

  const prerequisitesMet = !!(
    organization?.emailBranding?.fromName?.trim() &&
    organization?.emailBranding?.replyTo?.trim()
  );

  const { data, loading } = useOrganizationEmailDomainByOrganizationQuery({
    variables: { organizationId: organizationId ?? "" },
    skip: !flagEnabled || !organizationId || !prerequisitesMet,
  });
  const config = data?.organizationEmailDomainByOrganization ?? null;

  const [fromAddress, setFromAddress] = useState("");
  useEffect(() => {
    if (config?.fromAddress) setFromAddress(config.fromAddress);
  }, [config?.fromAddress]);

  const [requestDomain, { loading: isSubmitting }] =
    useOrganizationEmailDomainRequestMutation({
      refetchQueries: [OrganizationEmailDomainByOrganizationDocument],
    });

  const canRequest = !config || REQUESTABLE_STATUSES.has(config.status);
  const emailIsValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(fromAddress.trim());

  const onSubmit = async () => {
    if (!organizationId || !emailIsValid) return;
    try {
      const { errors } = await requestDomain({
        variables: { organizationId, fromAddress: fromAddress.trim() },
      });
      if (errors?.length) throw new Error(errors[0].message);
      toastFactory.create({
        title: tCore("core:SUCCESS"),
        description: t("REQUEST_SUCCESS"),
      });
    } catch (error) {
      trackPostHogException(error, {
        fileName: "useCustomEmailDomainPanel.ts",
        method: "onSubmit",
        message: "Error requesting custom email domain",
      });
      toastFactory.create({
        title: tCore("core:ERROR.TITLE"),
        description:
          error instanceof Error && error.message
            ? error.message
            : t("REQUEST_ERROR"),
      });
    }
  };

  return {
    flagEnabled,
    prerequisitesMet,
    loading,
    config,
    fromAddress,
    setFromAddress,
    emailIsValid,
    canRequest,
    isSubmitting,
    onSubmit,
  };
};
